import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { RecentSearches } from '@theme/recent-searches';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();

    const term = new URL(window.location.href).searchParams.get('q')?.trim() ?? '';
    if (!term) return;

    RecentSearches.addTerm(term);
    this.#showDidYouMean(term);
  }

  /**
   * When the search found nothing, asks Shopify's search suggestions for a corrected spelling and
   * offers it as a link. The [data-did-you-mean] slot is only rendered when there are no results.
   * @param {string} term - The search term from the URL
   */
  async #showDidYouMean(term) {
    const container = this.closest('form')?.querySelector('[data-did-you-mean]');
    const link = container?.querySelector('[data-did-you-mean-link]');
    if (!(container instanceof HTMLElement) || !(link instanceof HTMLAnchorElement)) return;

    const suggestUrl = new URL(`${Theme.routes.predictive_search_url}.json`, location.origin);
    suggestUrl.searchParams.set('q', term);
    suggestUrl.searchParams.set('resources[type]', 'query');
    suggestUrl.searchParams.set('resources[limit]', '3');

    try {
      const response = await fetch(suggestUrl);
      if (!response.ok) return;

      /** @type {{ resources?: { results?: { queries?: { text: string }[] } } }} */
      const data = await response.json();
      const suggestion = data.resources?.results?.queries
        ?.map((query) => query.text?.trim())
        .find((text) => text && text.toLowerCase() !== term.toLowerCase());
      if (!suggestion) return;

      // Keep the search type and matching options, drop filters and paging from the failed search
      const correctedUrl = new URL(window.location.href);
      for (const key of [...correctedUrl.searchParams.keys()]) {
        if (key !== 'type' && !key.startsWith('options[')) correctedUrl.searchParams.delete(key);
      }
      correctedUrl.searchParams.set('q', suggestion);

      link.textContent = suggestion;
      link.href = correctedUrl.toString();
      container.hidden = false;
    } catch {
      // No suggestion available: the regular no-results message stays
    }
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
